<script setup lang="ts">
import {
	type BrandsResponse,
	type DropsResponse,
	type EntityData,
	type LinkNode,
	mapBrandsToLinkNodes,
	mapDropsToLinkNodes,
} from '@laam/cms-shared';
import { computed, inject, onMounted, onUnmounted, ref, type Ref } from 'vue';
import { IconButton } from '../base';
import { PhCaretLeft, PhCaretRight } from '@phosphor-icons/vue';
import EntityCardV2 from './EntityCardV2.vue';

interface Props {
	metadata: EntityData;
	data: DropsResponse | BrandsResponse;
	sectionIndex: number;
	loading: boolean;
}

const storeType = inject('storeType') as Ref<string>;

const props = defineProps<Props>();

if (!Array.isArray(props.data)) {
	console.log('EntityCarousel: data is not an array', {
		...props,
	});
}

const entities = computed<LinkNode[]>(() => {
	if (props.metadata.entity === 'brands') {
		return mapBrandsToLinkNodes(
			props.data as BrandsResponse,
			props.metadata as EntityData,
		);
	} else if (props.metadata.entity === 'drops') {
		return mapDropsToLinkNodes(props.data as DropsResponse, storeType.value);
	} else {
		throw new Error('Unsupported entity');
	}
});

const entityCarouselRef = ref();

const autoScrollTimer = ref<ReturnType<typeof setInterval> | null>(null);

const startAutoscroll = () => {
	autoScrollTimer.value = setInterval(() => {
		if (!isNextDisabled.value) {
			handleNext();
		} else if (entityCarouselRef.value) {
			entityCarouselRef.value.slideTo(0);
		}
	}, 4000);
};

const stopAutoscroll = () => {
	if (autoScrollTimer.value) {
		clearInterval(autoScrollTimer.value);
		autoScrollTimer.value = null;
	}
};

onMounted(() => {
	startAutoscroll();
});

onUnmounted(() => {
	stopAutoscroll();
});

const deviceType = inject('deviceType') as Ref<string | undefined>;
const handleNext = () => {
	entityCarouselRef.value?.next();
};
const handlePrev = () => {
	entityCarouselRef.value?.prev();
};

const maxSlide = computed(() => {
	return entityCarouselRef.value?.data.maxSlide.value;
});

const currentSlide = computed(() => {
	return entityCarouselRef.value?.data.currentSlide.value;
});

const isPrevDisabled = computed(() => {
	return currentSlide.value == 0;
});

const isNextDisabled = computed(() => {
	return currentSlide.value >= maxSlide.value;
});

const dots = computed(() => {
	if (!maxSlide.value || currentSlide.value === undefined) return [];

	return [
		currentSlide.value === 0 ? 'bg-gray-700' : 'bg-gray-400',
		currentSlide.value > 0 && currentSlide.value < maxSlide.value
			? 'bg-gray-700'
			: 'bg-gray-400',
		currentSlide.value === maxSlide.value ? 'bg-gray-700' : 'bg-gray-400',
	];
});
</script>

<template>
	<div
		class="entity-carousel gap-3xl lg:gap-7xl lg:px-0 px-3xl flex flex-col items-center"
		@mouseover="stopAutoscroll"
		@mouseleave="startAutoscroll"
		@touchstart="stopAutoscroll"
		@touchend="startAutoscroll"
	>
		<div class="gap-sm flex w-full justify-between items-center">
			<div class="gap-sm flex flex-col w-full items-start">
				<h3
					ref="title"
					class="text-lg font-semibold lg:display-xs text-gray-900"
				>
					{{ metadata.title }}
				</h3>
			</div>
			<div class="flex flex-row w-full items-center justify-end">
				<div class="w-fit lg:mr-3xl">
					<slot name="selectPeriod" :entity="metadata.entity" />
				</div>
				<div v-if="deviceType !== 'mobile'" class="flex gap-xs pr-7xl">
					<IconButton
						variant="secondary"
						size="xs"
						rounded="full"
						:disabled="isPrevDisabled"
						@click="handlePrev"
					>
						<PhCaretLeft />
					</IconButton>
					<IconButton
						variant="secondary"
						size="xs"
						rounded="full"
						:disabled="isNextDisabled"
						@click="handleNext"
					>
						<PhCaretRight />
					</IconButton>
				</div>
			</div>
		</div>

		<div
			v-if="entities.length && !loading"
			class="cms--carousel-large-entity-base w-full"
		>
			<Carousel
				ref="entityCarouselRef"
				:items-to-show="deviceType === 'mobile' ? 2 : 3.3"
				snap-align="start"
			>
				<Slide
					v-for="(card, horizontalIndex) in entities"
					:key="horizontalIndex"
					:index="horizontalIndex"
				>
					<slot
						:entity="metadata.entity"
						:data="card"
						:horizontal-index="horizontalIndex"
					>
						<EntityCardV2
							:entity="metadata.entity"
							:data="card"
							:index="horizontalIndex"
						/>
					</slot>
				</Slide>
			</Carousel>
			<div class="lg:hidden flex gap-md items-center justify-center mt-3xl">
				<div
					v-for="(dotClass, index) in dots"
					:key="index"
					:class="[
						'size-md rounded-full transition-all duration-300 ease-in-out',
						dotClass,
					]"
				></div>
			</div>
		</div>
		<slot v-else-if="loading" name="skeleton" />
		<slot v-else name="noData" :entity="metadata.entity" />
	</div>
</template>

<style>
.cms--carousel-large-entity-base .carousel__prev {
	@apply bg-white rounded-full top-[49%] p-md mx-3xl lg:p-lg lg:mx-5xl;
}

.cms--carousel-large-entity-base .carousel__next {
	@apply bg-white rounded-full top-[49%] p-md mx-3xl lg:p-lg lg:ml-5xl lg:mr-9xl;
}

.cms--carousel-large-entity-base .cms--carousel-drops__item {
	@apply relative rounded-xs text-xl text-white bg-white w-full;
}

.cms--carousel-large-entity-base .carousel__slide {
	@apply pr-md lg:pr-7xl items-start flex flex-col;
}

.cms--carousel-large-entity-base .carousel__pagination {
	@apply absolute mx-auto left-[0px] right-[0px] gap-sm w-fit bottom-3xl;
}

.cms--carousel-large-entity-base .carousel__pagination-item {
	@apply box-border;
}

.cms--carousel-large-entity-base .carousel__pagination-button {
	@apply p-none;
}

.cms--carousel-large-entity-base .carousel__pagination-button::after {
	@apply bg-white rounded-2xl w-10xl h-xs opacity-40;
}

.cms--carousel-large-entity-base .carousel__pagination-button--active::after {
	@apply bg-white opacity-100;
}
</style>
